"use client";

import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useEffect, useState } from "react";
import axiosInstance from "@/utils/axiosInstance";

const chartConfig = {
  tour: {
      label: "Desktop",
      color: "hsl(var(--chart-1))",
  },
  commission_partner_id: {
    label: "Commission Partner",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

const CommissionPartnersLastMonths = () => {
  const [chartData, setChartData] = useState<
    { month: string; tours: number }[]
  >([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axiosInstance.get(
          "/reports-taxi/tours-by-commission-partner",
        );

        setChartData(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const getLastThreeMonthsRange = () => {
    const now = new Date();
    const months: { name: string; year: number }[] = [];

    for (let i = 2; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = new Intl.DateTimeFormat("en-US", {
        month: "long",
      }).format(date);
      const year = date.getFullYear();
      months.push({ name: monthName, year });
    }

    const first = months[0];
    const last = months[2];

    return `${first.name} ${first.year} - ${last.name} ${last.year}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tours by Commission Partners - Last 3 Months</CardTitle>
        <CardDescription>{getLastThreeMonthsRange()}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig}>
          <LineChart accessibilityLayer data={chartData}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => value}
            />
            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
            <Line
              dataKey="count"
              type="natural"
              stroke="#222"
              strokeWidth={2}
              dot={{
                fill: "var(--color-tours)",
              }}
              activeDot={{
                r: 6,
              }}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm">
        <div className="leading-none text-muted-foreground">
          Showing tours from commission partners for the last 3 months
        </div>
      </CardFooter>
    </Card>
  );
};

export default CommissionPartnersLastMonths;
